import { useEffect, useRef } from 'react';
import { useFormApi, useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { getCustomizedError } from 'lib/aiTools';
import { Avatar } from 'components/common';
import { Box } from 'components/common/box';
import { DisplayField } from 'components/common/form';
import { CheckboxInput } from 'components/common/form/common';

import type { GetTextCompletionRes } from 'shared-types/responses';
import type { FormValues } from './AIGenerateContentButton';

interface Props {
  blocks: GetTextCompletionRes['blocks'];
  languageVersion: string;
  visible: boolean;
}

const AIGenerateContentResults = ({
  blocks,
  languageVersion,
  visible
}: Props) => {
  const formApi = useFormApi<FormValues>();
  const { values } = useFormState<FormValues>();

  const { messages, theme } = useReduxState();

  const blocksByType = useRef(null);

  useEffect(() => {
    getBlocks();
  }, []);

  useEffect(() => {
    if (visible && blocks.length) {
      formApi.setValue('selectedBlocks', []);

      if (
        blocks.length > 1 ||
        (blocks.length === 1 && blocks[0].hasOwnProperty('error'))
      ) {
        formApi.setError('selectedBlocks', 'invalid');
      } else formApi.setValue('selectedBlocks', [0]);
    } else if (!visible) {
      formApi.setError('selectedBlocks', undefined);
    }
  }, [blocks, visible]);

  const getBlocks = async () => {
    const { EditorBlockText } = await import(`components/editor/blocks/text`);
    const { EditorBlockHeading } = await import(
      `components/editor/blocks/heading`
    );
    const { EditorBlockQuote } = await import(`components/editor/blocks/quote`);
    const { EditorBlockList } = await import(`components/editor/blocks/list`);
    const { EditorBlockNumericQuestion } = await import(
      `components/editor/blocks/numericQuestion`
    );
    const { EditorBlockOpenQuestion } = await import(
      `components/editor/blocks/openQuestion`
    );
    const { EditorBlockFillIn } = await import(
      `components/editor/blocks/fillIn`
    );
    const { EditorBlockSequencing } = await import(
      `components/editor/blocks/sequencing`
    );
    const { EditorBlockMultipleChoice } = await import(
      `components/editor/blocks/multipleChoice`
    );
    const { EditorBlockSingleChoice } = await import(
      `components/editor/blocks/singleChoice`
    );

    blocksByType.current = {
      heading: EditorBlockHeading,
      list: EditorBlockList,
      quote: EditorBlockQuote,
      text: EditorBlockText,
      numericquestion: EditorBlockNumericQuestion,
      openquestion: EditorBlockOpenQuestion,
      fillin: EditorBlockFillIn,
      sequencing: EditorBlockSequencing,
      multiplechoice: EditorBlockMultipleChoice,
      singlechoice: EditorBlockSingleChoice
    };
  };

  const handleSelect = (index: number, selected: boolean) => {
    let selectedBlocks = [...values.selectedBlocks];
    if (selected) selectedBlocks = [...selectedBlocks, index];
    else selectedBlocks = selectedBlocks.filter((i) => i !== index);

    formApi.setValue('selectedBlocks', selectedBlocks);

    if (selectedBlocks.length === 0) {
      formApi.setError('selectedBlocks', 'invalid');
    } else formApi.setError('selectedBlocks', undefined);
  };
  const BlockType = blocksByType?.current?.[values.type];

  const isValid = blocks.length > 0;
  const isError = blocks.length === 1 && blocks[0].hasOwnProperty('errors');

  return (
    <div className="results">
      {!isValid ? null : isError ? (
        <div className="error">
          <Box padding={0}>
            <div className="error-inner">
              <div className="error-icon">
                <Avatar
                  backgroundColor={theme.dangerLightColor}
                  color={theme.dangerLightTextColor}
                  icon="exclamation-triangle"
                  padding="7px 0"
                  size="32px"
                />
              </div>
              <div className="error-title">
                {messages.aiGenerateContentError}
              </div>
              <div className="error-list">
                {(blocks[0] as { errors: string[] }).errors?.map(
                  (error, errorIndex) => (
                    <div className="error" key={errorIndex}>
                      {`${errorIndex + 1}. ${getCustomizedError(
                        messages,
                        error
                      )}`}
                    </div>
                  )
                )}
              </div>
            </div>
          </Box>
        </div>
      ) : (
        <>
          <div className="header">
            <Box padding={0}>
              <div className="header-inner">
                <div className="header-icon">
                  <Avatar
                    icon="wand-magic-sparkles"
                    padding="8px 0"
                    size="32px"
                  />
                </div>
                <div className="header-content">
                  <div className="header-title">
                    {messages.aiGenerateContentResults}
                  </div>
                  <div className="header-description">
                    {messages.aiGenerateContentResultsDescription}
                  </div>
                </div>
              </div>
            </Box>
          </div>

          <div className="blocks">
            {blocks.map((block, blockIndex) => (
              <div className="block" key={blockIndex}>
                <Box padding={0}>
                  <div className="block-inner">
                    {blocks.length > 1 ? (
                      <div className="block-header">
                        <div className="block-header-title">
                          {`${messages.commonAlternative} ${blockIndex + 1}`}
                        </div>
                        <div className="block-header-accept">
                          <CheckboxInput
                            checked={values.selectedBlocks.includes(blockIndex)}
                            id={`generated-block-select-${blockIndex}`}
                            label={messages.commonAccept}
                            onChange={(ev) =>
                              handleSelect(
                                blockIndex,
                                (ev.target as HTMLInputElement).checked
                              )
                            }
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="block-content">
                      <BlockType
                        blockId={0}
                        data={block}
                        isAIPreview={true}
                        languageVersion={languageVersion}
                        type={values.type}
                        updateData={() => undefined}
                      />
                    </div>
                  </div>
                </Box>
              </div>
            ))}
          </div>
        </>
      )}

      <DisplayField field="selectedBlocks" />

      <style jsx>{`
        .results {
          background-color: ${theme.lightGreyLighter};
          display: ${visible ? 'block' : 'none'};
          padding: 15px;
        }
        .error-inner {
          align-items: center;
          display: flex;
          flex-direction: column;
          gap: 10px;
          justify-content: center;
          margin: 0 auto;
          max-width: 500px;
          padding: 25px 15px;
          text-align: center;
        }
        .header-inner {
          display: flex;
          gap: 10px;
          padding: 10px 15px;
        }
        .header-icon {
          padding-top: 4px;
        }
        .header-title {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 600;
        }
        .header-description {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 400;
          margin-top: 2px;
        }
        .blocks {
          display: flex;
          flex-direction: column;
          gap: 25px;
          margin-top: 20px;
        }
        .block-header {
          align-items: center;
          border-bottom: 1px solid ${theme.lightGrey};
          display: flex;
          gap: 15px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .block-header-title {
          color: ${theme.textColor};
        }
        .block-header-accept {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.buttonBorderRadius};
          padding: 3px 6px;
        }
        .block-content {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 400;
          padding: 20px;
        }
      `}</style>
    </div>
  );
};

export default AIGenerateContentResults;
